import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NoSymbolIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { accountsApi } from '../accounts/api/accounts';
import { contactsApi } from '../contacts/api/contacts';
import { makeAuthenticatedRequest } from '../../core/api/client';
import { exportApi } from '../../core/api/exportApi';
import { getDivisionLabel, getDivisionConfig } from '../../config/divisions';
import { useAccessControl, PermissionGuard } from '../../contexts/AccessControlContext';
import EditAccountModal from '../accounts/EditAccountModal';
import BlacklistConfirmationModal from '../accounts/BlacklistConfirmationModal';
import MergeAccountModal from '../accounts/MergeAccountModal';
import AddContactModal from '../contacts/AddContactModal';
import EditContactModal from '../contacts/EditContactModal';
import AddCustomerModal from './AddCustomerModal';
import CustomerHistoryTab from './CustomerHistoryTab';
import CustomerPriceHistoryTab from './CustomerPriceHistoryTab';
import CustomerDocumentsTab from './CustomerDocumentsTab';
import CustomerRequirementsTab from './CustomerRequirementsTab';

const TABS = [
  { id: 'contacts', label: 'Contacts' },
  { id: 'history', label: 'History' },
  { id: 'prices', label: 'Price History' },
  { id: 'documents', label: 'Documents' },
  { id: 'requirements', label: 'Requirements' },
];

const TIER_BADGE_COLORS = {
  Golden: 'bg-amber-100 text-amber-800',
  Silver: 'bg-slate-200 text-slate-700',
  Black: 'bg-gray-800 text-white',
};

const ContactCard = ({ contact, canEdit, onEdit }) => (
  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-center min-w-0">
        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
          {contact.Name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div className="ml-3 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{contact.Name || '—'}</p>
          {contact.Designation && <p className="text-xs text-gray-500 truncate">{contact.Designation}</p>}
        </div>
      </div>
      {canEdit && (
        <button
          onClick={() => onEdit(contact)}
          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
          title="Edit contact"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      )}
    </div>
    <div className="mt-3 space-y-1">
      {contact.Email1 && (
        <a href={`mailto:${contact.Email1}`} className="block text-xs text-blue-600 hover:underline truncate">
          {contact.Email1}
        </a>
      )}
      {contact.Phone1 && <p className="text-xs text-gray-700">{contact.Phone1}</p>}
      {!contact.Email1 && !contact.Phone1 && <p className="text-xs text-gray-400">No contact details</p>}
    </div>
  </div>
);

const CustomersPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [activeTab, setActiveTab] = useState('contacts');

  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [isEditAccountOpen, setIsEditAccountOpen] = useState(false);
  const [isBlacklistOpen, setIsBlacklistOpen] = useState(false);
  const [isBlacklisting, setIsBlacklisting] = useState(false);
  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const deepLinkedIdRef = useRef(null);

  const { canEditAccounts, canBlacklistAccounts, canEditContacts } = useAccessControl();

  const fetchCustomers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const accountData = await accountsApi.getAllAccounts();
      setAccounts(accountData);
      setContacts([]);
    } catch (err) {
      setError('Failed to fetch customers. Please try again later.');
      console.error('Error fetching customers:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  // Load contacts for visible accounts (lazy)
  useEffect(() => {
    if (!accounts.length) return;
    const visibleIds = accounts.map(a => a.id || a.ID);
    if (!visibleIds.length) return;
    const token = localStorage.getItem('access_token');
    fetch('/api/v1/contacts/batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
      body: JSON.stringify({ account_ids: visibleIds })
    }).then(r => r.json()).then(data => {
      if (Array.isArray(data)) setContacts(data);
    }).catch(() => {});
  }, [accounts]);

  // Preserve the /accounts?accountId= and /contacts?contactId= deep links.
  useEffect(() => {
    if (!accounts.length) return;
    const params = new URLSearchParams(location.search);
    const accountId = params.get('accountId');
    const contactId = params.get('contactId');

    const targetId = accountId
      || (contactId && String(contacts.find((c) => String(c.id) === String(contactId))?.AccountID || ''));

    if (!targetId || deepLinkedIdRef.current === targetId) return;
    if (!accounts.some((a) => String(a.id) === String(targetId))) return;

    deepLinkedIdRef.current = targetId;
    setSelectedAccountId(Number(targetId));
  }, [accounts, contacts, location.search]);

  const contactsByAccount = useMemo(() => {
    const map = new Map();
    contacts.forEach((contact) => {
      const key = contact.AccountID || contact.account_id;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(contact);
    });
    return map;
  }, [contacts]);

  // A customer matches on its own name or on the name of any of its contacts.
  const filteredAccounts = useMemo(() => {
    const byTier = tierFilter ? accounts.filter((account) => account.tier === tierFilter) : accounts;
    const term = searchTerm.trim().toLowerCase();
    if (!term) return byTier;

    const matched = byTier.filter((account) => {
      if (account.Name?.toLowerCase().includes(term)) return true;
      if (account.Location?.toLowerCase().includes(term)) return true;
      return (contactsByAccount.get(account.id) || []).some((contact) =>
        contact.Name?.toLowerCase().includes(term) ||
        contact.Email1?.toLowerCase().includes(term) ||
        String(contact.Phone1 || '').toLowerCase().includes(term)
      );
    });
    // Rank: company-name matches first, then everything else
    const nameStarts = [];
    const nameHas = [];
    const other = [];
    matched.forEach((a) => {
      const n = a.Name?.toLowerCase() || '';
      if (n.startsWith(term)) nameStarts.push(a);
      else if (n.includes(term)) nameHas.push(a);
      else other.push(a);
    });
    return [...nameStarts, ...nameHas, ...other];
  }, [accounts, contactsByAccount, searchTerm, tierFilter]);

  const selectedAccount = useMemo(
    () => accounts.find((account) => account.id === selectedAccountId) || null,
    [accounts, selectedAccountId]
  );

  const selectedContacts = useMemo(
    () => (selectedAccountId ? contactsByAccount.get(selectedAccountId) || [] : []),
    [contactsByAccount, selectedAccountId]
  );

  const handleSelectAccount = async (accountId) => {
    setSelectedAccountId(accountId);
    setActiveTab('contacts');
    // Load contacts for this specific account
    try {
      const accountContacts = await contactsApi.getContactsByCompanyId(accountId);
      setContacts(prev => {
        const filtered = prev.filter(c => (c.AccountID || c.account_id) !== accountId);
        return [...filtered, ...accountContacts];
      });
    } catch(e) {
      console.error('Failed to load contacts:', e);
    }
  };

  const handleBackToList = () => {
    setSelectedAccountId(null);
    deepLinkedIdRef.current = null;
    if (location.search) navigate('/customers', { replace: true });
  };

  const handleAccountUpdated = async (accountId, accountData) => {
    await accountsApi.updateAccount(accountId, accountData);
    await fetchCustomers();
  };

  const handleBlacklistToggle = async (blacklistData) => {
    await accountsApi.toggleBlacklist(selectedAccount.id, blacklistData);
    await fetchCustomers();
  };

  const handleMergeConfirm = async (sourceId, targetId) => {
    const response = await makeAuthenticatedRequest(`/api/v1/accounts/${sourceId}/merge-into/${targetId}`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Merge failed');
    // The source account is gone after a merge; follow the surviving one.
    setSelectedAccountId(Number(targetId));
    await fetchCustomers();
  };

  const handleContactCreated = async (contactData) => {
    await contactsApi.createContact(contactData);
    await fetchCustomers();
  };

  const handleContactUpdated = async (contactId, contactData) => {
    await contactsApi.updateContact(contactId, contactData);
    await fetchCustomers();
  };

  const handleCustomerAdded = async (accountId) => {
    await fetchCustomers();
    setSelectedAccountId(accountId);
    setActiveTab('contacts');
  };

  if (error && !accounts.length) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <span className="text-red-400 mr-2">⚠️</span>
            <div>
              <h3 className="text-red-800 font-medium">Error Loading Customers</h3>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          </div>
          <button
            onClick={fetchCustomers}
            className="mt-3 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
            <p className="text-gray-600 mt-1">Companies and the people you deal with</p>
          </div>
          <div className="flex items-center gap-3">
          <button
            onClick={() => exportApi.downloadExcel('customers').catch((e) => alert('Export failed: ' + e.message))}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
          >
            Export to Excel
          </button>
          <PermissionGuard resource="accounts" action="create">
            <button
              onClick={() => setIsAddCustomerOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              <span className="mr-2">+</span>
              Add Customer
            </button>
          </PermissionGuard>
          </div>
        </div>

        {selectedAccount ? (
          <CustomerDetail
            account={selectedAccount}
            contacts={selectedContacts}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onBack={handleBackToList}
            canEditAccounts={canEditAccounts}
            canBlacklistAccounts={canBlacklistAccounts}
            canEditContacts={canEditContacts}
            onEditAccount={() => setIsEditAccountOpen(true)}
            onMergeAccount={() => setIsMergeOpen(true)}
            onBlacklistAccount={() => {
              setIsBlacklisting(!selectedAccount.blacklist);
              setIsBlacklistOpen(true);
            }}
            onAddContact={() => setIsAddContactOpen(true)}
            onEditContact={setEditingContact}
          />
        ) : (
          <CustomerList
            accounts={filteredAccounts}
            contactsByAccount={contactsByAccount}
            isLoading={isLoading}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            tierFilter={tierFilter}
            onTierFilterChange={setTierFilter}
            onSelect={handleSelectAccount}
            onAddCustomer={() => setIsAddCustomerOpen(true)}
          />
        )}

        <AddCustomerModal
          isOpen={isAddCustomerOpen}
          onClose={() => setIsAddCustomerOpen(false)}
          onCustomerAdded={handleCustomerAdded}
        />

        <EditAccountModal
          isOpen={isEditAccountOpen}
          onClose={() => setIsEditAccountOpen(false)}
          onAccountUpdated={handleAccountUpdated}
          account={selectedAccount}
        />

        <BlacklistConfirmationModal
          isOpen={isBlacklistOpen}
          onClose={() => { setIsBlacklistOpen(false); setIsBlacklisting(false); }}
          onConfirm={handleBlacklistToggle}
          account={selectedAccount}
          isBlacklisting={isBlacklisting}
        />

        <MergeAccountModal
          isOpen={isMergeOpen}
          onClose={() => setIsMergeOpen(false)}
          sourceAccount={selectedAccount}
          allAccounts={accounts}
          onMerge={handleMergeConfirm}
        />

        <AddContactModal
          isOpen={isAddContactOpen}
          onClose={() => setIsAddContactOpen(false)}
          onContactAdded={handleContactCreated}
          accounts={accounts}
        />

        <EditContactModal
          isOpen={Boolean(editingContact)}
          onClose={() => setEditingContact(null)}
          onContactUpdated={handleContactUpdated}
          contact={editingContact}
          accounts={accounts}
        />
      </div>
    </div>
  );
};

const CustomerList = ({
  accounts,
  contactsByAccount,
  isLoading,
  searchTerm,
  onSearchChange,
  tierFilter,
  onTierFilterChange,
  onSelect,
  onAddCustomer,
}) => (
  <>
    <div className="mb-3 relative">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">🔍</span>
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search by company, contact, phone, email, or city..."
        className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
    <div className="flex items-center gap-2 mb-6">
      {['', 'Golden', 'Silver', 'Black'].map((t) => (
        <button
          key={t || 'all'}
          type="button"
          onClick={() => onTierFilterChange(t)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            tierFilter === t
              ? (t ? TIER_BADGE_COLORS[t] + ' border-transparent' : 'bg-blue-600 text-white border-transparent')
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
          }`}
        >
          {t || 'All tiers'}
        </button>
      ))}
    </div>

    {isLoading && (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
        Loading customers...
      </div>
    )}

    {!isLoading && accounts.length === 0 && (
      <div className="text-center py-16 bg-white border border-gray-200 rounded-xl">
        <div className="text-3xl mb-2">🏢</div>
        {searchTerm ? (
          <>
            <p className="text-sm text-gray-600">No customers match “{searchTerm}”.</p>
            <button
              onClick={() => onSearchChange('')}
              className="mt-3 text-xs font-medium text-blue-700 hover:text-blue-900"
            >
              Clear search
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600">No customers yet.</p>
            <button
              onClick={onAddCustomer}
              className="mt-3 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors"
            >
              Add Customer
            </button>
          </>
        )}
      </div>
    )}

    {!isLoading && accounts.length > 0 && (
      <>
        <p className="text-xs text-gray-500 mb-3">
          {accounts.length} customer{accounts.length !== 1 ? 's' : ''}
        </p>
        <div className="space-y-3">
          {accounts.map((account) => {
            const accountContacts = contactsByAccount.get(account.id) || [];
            const division = getDivisionConfig(account.Division);
            return (
              <button
                key={account.id}
                type="button"
                onClick={() => onSelect(account.id)}
                className="w-full text-left bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
                      {account.Name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="ml-3 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">{account.Name}</p>
                        {account.blacklist && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Blacklisted
                          </span>
                        )}
                      </div>
                      {account.Location && <p className="text-xs text-gray-500 truncate">{account.Location}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${division.badgeColor}`}>
                      {getDivisionLabel(account.Division)}
                    </span>
                    {account.tier && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${TIER_BADGE_COLORS[account.tier] || 'bg-gray-100 text-gray-700'}`}>
                        {account.tier}
                      </span>
                    )}
                  </div>
                </div>

                {accountContacts.length > 0 ? (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {accountContacts.map((contact) => (
                      <div key={contact.id} className="text-xs bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                        <p className="font-medium text-gray-800 truncate">{contact.Name || '—'}</p>
                        {contact.Email1 && <p className="text-blue-600 truncate">{contact.Email1}</p>}
                        {contact.Phone1 && <p className="text-gray-600">{contact.Phone1}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-xs text-gray-400">No contacts yet</p>
                )}
              </button>
            );
          })}
        </div>
      </>
    )}
  </>
);

const CustomerDetail = ({
  account,
  contacts,
  activeTab,
  onTabChange,
  onBack,
  canEditAccounts,
  canBlacklistAccounts,
  canEditContacts,
  onEditAccount,
  onMergeAccount,
  onBlacklistAccount,
  onAddContact,
  onEditContact,
}) => {
  const division = getDivisionConfig(account.Division);

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm text-blue-700 hover:text-blue-900 font-medium">
        ← Back to customers
      </button>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center min-w-0">
            <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-lg font-semibold flex-shrink-0">
              {account.Name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="ml-4 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900 truncate">{account.Name}</h2>
                {account.blacklist && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Blacklisted</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${division.badgeColor}`}>
                  {getDivisionLabel(account.Division)}
                </span>
                {account.tier && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TIER_BADGE_COLORS[account.tier] || 'bg-gray-100 text-gray-700'}`}>
                    {account.tier}
                  </span>
                )}
                {account.Industry && <span className="text-xs text-gray-500">{account.Industry}</span>}
                {account.Location && <span className="text-xs text-gray-500">· {account.Location}</span>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {canEditAccounts && (
              <button
                onClick={onEditAccount}
                className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Edit
              </button>
            )}
            {canBlacklistAccounts && (
              <>
                <button
                  onClick={onMergeAccount}
                  className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-orange-50 hover:text-orange-700 transition-colors"
                >
                  Merge
                </button>
                <button
                  onClick={onBlacklistAccount}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-lg transition-colors ${
                    account.blacklist
                      ? 'text-gray-700 hover:bg-green-50 hover:text-green-700'
                      : 'text-gray-700 hover:bg-red-50 hover:text-red-700'
                  }`}
                >
                  {account.blacklist ? (
                    <><ArrowUturnLeftIcon className="w-3.5 h-3.5" /> Un-blacklist</>
                  ) : (
                    <><NoSymbolIcon className="w-3.5 h-3.5" /> Blacklist</>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="border-b border-gray-200 bg-white rounded-t-xl">
        <nav className="flex space-x-8 px-5">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.id === 'contacts' && contacts.length > 0 && (
                <span className="ml-2 text-xs text-gray-400">{contacts.length}</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'contacts' && (
        <div className="space-y-4">
          <PermissionGuard resource="contacts" action="create">
            <div className="flex justify-end">
              <button
                onClick={onAddContact}
                className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                + Add Contact
              </button>
            </div>
          </PermissionGuard>

          {contacts.length === 0 ? (
            <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
              <div className="text-3xl mb-2">👤</div>
              <p className="text-sm text-gray-600">No contacts for this customer yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {contacts.map((contact) => (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  canEdit={canEditContacts}
                  onEdit={onEditContact}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && <CustomerHistoryTab accountId={account.id} />}
      {activeTab === 'prices' && <CustomerPriceHistoryTab customerName={account.Name} />}
      {activeTab === 'documents' && <CustomerDocumentsTab accountId={account.id} />}
      {activeTab === 'requirements' && <CustomerRequirementsTab accountId={account.id} />}
    </div>
  );
};

export default CustomersPage;
